import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CATEGORY_ICON_NAMES,
  CategoryIcon,
  resolveCategoryIconName,
} from '../../utils/categoryIcons';

interface CategoryIconPickerProps {
  value: string;
  onChange: (iconName: string) => void;
}

const CategoryIconPicker: React.FC<CategoryIconPickerProps> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const selectedIconName = resolveCategoryIconName(value);

  return (
    <FormControl fullWidth>
      <FormLabel sx={{ mb: 1 }}>{t('openingHourCategories.icon')}</FormLabel>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <CategoryIcon iconName={selectedIconName} selected />
        <Typography variant="body2" color="text.secondary">
          {selectedIconName}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(44px, 1fr))',
          gap: 0.5,
          p: 1,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          maxHeight: 180,
          overflowY: 'auto',
        }}
      >
        {CATEGORY_ICON_NAMES.map((iconName) => {
          const isSelected = selectedIconName === iconName;

          return (
            <Tooltip key={iconName} title={iconName} arrow>
              <IconButton
                size="small"
                onClick={() => onChange(iconName)}
                aria-label={iconName}
                aria-pressed={isSelected}
                sx={{
                  border: 1,
                  borderColor: isSelected ? 'primary.main' : 'transparent',
                  bgcolor: isSelected ? 'action.selected' : 'transparent',
                }}
              >
                <CategoryIcon iconName={iconName} fontSize={20} selected={isSelected} />
              </IconButton>
            </Tooltip>
          );
        })}
      </Box>
      <FormHelperText>{t('openingHourCategories.iconHelp')}</FormHelperText>
    </FormControl>
  );
};

export default CategoryIconPicker;
